/* 모임 기록 (/meeting/:meetingId/journal)
 *
 * 메인탭 오늘 카드와 모임 상세에서 들어옵니다.
 * 읽는 방식이 두 가지라 탭으로 나눕니다.
 *   시간순  여행 중에는 방금 올라온 것이 궁금합니다
 *   일차별  여행이 끝난 뒤에는 정리된 기록으로 읽힙니다
 *
 * 쓰는 자리를 맨 위에 둡니다 — 읽다가 쓰고 싶어졌을 때 위로 올라가지
 * 않아도 되게.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppScreen, Section, SectionHeader, Tabs, EmptyState, Button } from 'components/ds';
import { IcCamera } from 'components/common/icons/EmojiIcons';
import { MeetingJournalSection } from 'components/journal/MeetingJournalSection';
import { JournalEntryCard } from 'components/journal/JournalEntryCard';
import { useMeetingJournal } from 'hooks/useMeetingJournal';
import type { JournalEntry } from 'hooks/useMeetingJournal';
import { VisitPhotoWrite } from 'components/place/VisitPhotoWrite';
import { BlockConfirmModal } from 'components/common/BlockConfirmModal';
import { ReportSheet } from 'components/common/ReportSheet';
import type { ReportReasonCode } from 'components/common/ReportSheet';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { BlockRepository } from 'repository/blockRepository';
import { useAlert } from 'contexts/AlertContext';
import { useUserInfo } from 'hooks/useAuth';
import styles from './MeetingJournalPage.module.css';

export const MeetingJournalPage = () => {
    const { t } = useTranslation();
    const { meetingId = '' } = useParams();
    const [search] = useSearchParams();

    /* 앵커(어디에 남길지)는 들어온 화면이 판단해서 넘겨 줍니다.
       여기서 다시 계산하면 같은 판단이 두 곳으로 갈라집니다. */
    const placeId = search.get('placeId') || '';
    const placeName = search.get('placeName') || '';
    const dayNumber = search.get('day') ? Number(search.get('day')) : undefined;
    const subId = search.get('subId') ? Number(search.get('subId')) : undefined;

    const { nickname, profileImgPath } = useUserInfo();
    const [sort, setSort] = useState<'time' | 'day'>('time');
    const { entries, byDay, loading, hasMore, reload, loadMore } = useMeetingJournal(meetingId, sort);
    const { showAlert, showConfirm } = useAlert();

    /* 더보기 메뉴가 띄우는 것들. 카드가 아니라 화면이 들고 있습니다 */
    const [editing, setEditing] = useState<JournalEntry | null>(null);
    const [reporting, setReporting] = useState<JournalEntry | null>(null);
    const [blocking, setBlocking] = useState<JournalEntry | null>(null);

    /** onRequest 는 에러를 던지지 않습니다 — 상태 코드를 봐야 실패를 압니다 */
    const ok2xx = (res: any) =>
        typeof res?.status === 'number' && res.status >= 200 && res.status < 300;

    const removeEntry = useCallback(async (entry: JournalEntry) => {
        const yes = await showConfirm({ message: t('visitPhoto.deleteConfirm') });
        if (!yes) return;
        const ok = ok2xx(await PlaceReviewRepository.deleteReview(entry.reviewId));
        await showAlert({ message: t(ok ? 'visitPhoto.deleted' : 'visitPhoto.deleteFail') });
        if (ok) reload();
    }, [showConfirm, showAlert, t, reload]);

    const sendReport = useCallback(async (
        reviewId: number, code: ReportReasonCode, text?: string,
    ) => ok2xx(await PlaceReviewRepository.reportReview(reviewId, code, text)), []);

    const reportEntry = useCallback(async (code: ReportReasonCode, text: string) => {
        if (!reporting) return;
        const ok = await sendReport(reporting.reviewId, code, text);
        setReporting(null);
        await showAlert({ message: t(ok ? 'posts.reportDone' : 'posts.reportFail') });
        /* 신고가 쌓여 내려갔을 수도 있으니 다시 받습니다 */
        if (ok) reload();
    }, [reporting, sendReport, showAlert, t, reload]);

    const blockAuthor = useCallback(async (reason?: string, alsoReport?: boolean) => {
        const target = blocking;
        if (!target) return;
        try {
            await BlockRepository.onPostBlock(target.userNo, reason);
            /* 인앱 알림 배너 등 다른 화면에도 알립니다 — MemberActionMenu 와 같은 신호 */
            window.dispatchEvent(new CustomEvent('dokocrew:blockChange', {
                detail: { userNo: target.userNo },
            }));
            /* 차단은 나만 안 보는 것, 신고는 모두에게서 내리는 것 — 따로 나갑니다.
               신고가 실패해도 차단은 이미 됐으므로 되돌리지 않습니다. */
            if (alsoReport) {
                try { await sendReport(target.reviewId, 'ETC', reason); }
                catch (re) { console.warn('신고 적재 실패', re); }
            }
            await showAlert({ message: t('visitPhoto.blocked', { name: target.nickname || '' }) });
            reload();
        } catch (e: any) {
            await showAlert({ message: e?.response?.data?.message || t('visitPhoto.blockFail') });
        }
    }, [blocking, sendReport, showAlert, t, reload]);

    const anchor = useMemo(() => (placeId ? {
        placeId,
        placeName,
        dayNumber,
        subId,
        label: dayNumber ? `${dayNumber}${t('journal.dayUnit')} · ${placeName}` : placeName,
    } : null), [placeId, placeName, dayNumber, subId, t]);

    const handlePosted = useCallback(() => { reload(); }, [reload]);

    return (
        <AppScreen title={t('journal.sectionTitle')} bar="sub">
            <Section spacing="tight">
                <Tabs
                    items={[
                        { value: 'time', label: t('journal.sortTime') },
                        { value: 'day', label: t('journal.sortDay') },
                    ]}
                    value={sort}
                    onChange={(v) => setSort(v as 'time' | 'day')}
                    ariaLabel={t('journal.sortAria')}
                />
            </Section>

            {anchor && (
                <Section spacing="tight">
                    <MeetingJournalSection
                        meetingId={meetingId}
                        anchor={anchor}
                        nickname={nickname}
                        profileUrl={profileImgPath}
                        onPosted={handlePosted}
                    />
                </Section>
            )}

            {entries.length === 0 && !loading ? (
                <Section>
                    <EmptyState
                        icon={<IcCamera size={22} />}
                        title={t('journal.empty')}
                        body={t('journal.emptyBody')}
                    />
                </Section>
            ) : sort === 'day' ? (
                byDay.map(({ day, items }) => (
                    <Section key={String(day)} divided>
                        <SectionHeader
                            title={day ? `Day ${day}` : t('journal.noDay')}
                            sub={t('journal.dayCount', { count: items.length })}
                        />
                        <ul className={styles.list}>
                            {items.map(e => (
                                <JournalEntryCard key={e.reviewId} entry={e} hideDay onChanged={reload}
                                                  onEdit={setEditing} onDelete={removeEntry}
                                                  onReport={setReporting} onBlock={setBlocking} />
                            ))}
                        </ul>
                    </Section>
                ))
            ) : (
                <Section>
                    <ul className={styles.list}>
                        {entries.map(e => (
                            <JournalEntryCard key={e.reviewId} entry={e} onChanged={reload}
                                              onEdit={setEditing} onDelete={removeEntry}
                                              onReport={setReporting} onBlock={setBlocking} />
                        ))}
                    </ul>
                </Section>
            )}

            {hasMore && (
                <Section spacing="tight">
                    <Button variant="secondary" size="md" block onClick={loadMore} disabled={loading}>
                        {loading ? t('common.loading') : t('common.more')}
                    </Button>
                </Section>
            )}

            {/* 고치기 — 쓰기 시트를 지금 값으로 채워 엽니다.
                사진은 수정 API 가 받지 않아 시트가 그 칸을 감춥니다. */}
            <VisitPhotoWrite
                open={editing !== null}
                editing
                onClose={() => setEditing(null)}
                placeName={editing?.placeName || ''}
                initial={editing ? { rating: editing.rating ?? 0, content: editing.content } : undefined}
                onSubmit={async (draft) => {
                    if (!editing) return;
                    const ok = ok2xx(await PlaceReviewRepository.updateReview(editing.reviewId, {
                        placeId: editing.placeId,
                        rating: draft.rating,
                        content: draft.content,
                        tags: draft.tags.join(','),
                    }));
                    setEditing(null);
                    await showAlert({ message: t(ok ? 'visitPhoto.updated' : 'visitPhoto.updateFail') });
                    if (ok) reload();
                }}
            />

            <ReportSheet
                open={reporting !== null}
                onClose={() => setReporting(null)}
                onSubmit={reportEntry}
            />

            <BlockConfirmModal
                isOpen={blocking !== null}
                targetName={blocking?.nickname || ''}
                onClose={() => setBlocking(null)}
                onConfirm={({ reason, report }) => blockAuthor(reason, report)}
            />
        </AppScreen>
    );
};
